/**
 * Showing LEDGER money (what the wallet, bets and bonuses are kept in) in
 * another currency — the site's display currency, or a house account's.
 *
 * A rate is units of the OTHER currency per ONE ledger unit, as the decimal
 * string the API sends: `'700'` on a LAK site under a THB ledger is 700 LAK = 1
 * THB. Arithmetic is scaled bigint and truncates toward zero in both
 * directions, so a converted figure never exceeds what the ledger holds.
 */
import { fromLedgerMinor, parseRate, toLedgerMinor } from '@b1fr0st/shared/money-rate';
import { formatMoney } from './index.js';

/**
 * `'712.5'` → `712500000n`. Null for anything malformed, zero, negative or with
 * a seventh decimal — `@b1fr0st/shared/money-rate`, the copy svc-finance uses.
 */
export function parseRateScaled(rate: string | null | undefined): bigint | null {
  return parseRate(rate);
}

/**
 * Ledger money as it should read in `targetCurrency` at `rate`.
 *
 * `currency` is what the figures actually come out in: `targetCurrency` when
 * the two currencies match (identity) or the rate is usable, otherwise the
 * LEDGER currency — a missing rate falls back to the true figure rather than an
 * invented 1:1. `rate` is null exactly when nothing is converted.
 */
export interface MoneyView {
  ledgerCurrency: string;
  currency: string;
  rate: string | null;
  toTarget: (ledgerMinor: bigint) => bigint;
  toLedger: (targetMinor: bigint) => bigint;
  /** Format a ledger amount in `currency`. Minor units arrive as bigint; see `minorFromWire`. */
  format: (ledgerMinor: bigint) => string;
}

export function moneyView(
  ledgerCurrency: string,
  targetCurrency: string | null | undefined,
  rate: string | null | undefined,
): MoneyView {
  const ledger = ledgerCurrency.toUpperCase();
  const target = (targetCurrency ?? ledger).toUpperCase();
  const scaled = target === ledger ? null : parseRateScaled(rate);
  if (scaled === null) {
    return {
      ledgerCurrency: ledger,
      currency: ledger,
      rate: null,
      toTarget: (m) => m,
      toLedger: (m) => m,
      format: (m) => formatMoney(m, ledgerCurrency),
    };
  }
  const toTarget = (m: bigint) => fromLedgerMinor(m, ledger, target, scaled);
  return {
    ledgerCurrency: ledger,
    currency: target,
    rate: rate!.trim(),
    toTarget,
    toLedger: (m) => toLedgerMinor(m, target, ledger, scaled),
    format: (m) => formatMoney(toTarget(m), target),
  };
}

/** `'700 LAK = 1 THB'` — how a rate is stated to a player. */
export function rateLabel(rate: string, currency: string, ledgerCurrency: string): string {
  return `${rate} ${currency.toUpperCase()} = 1 ${ledgerCurrency.toUpperCase()}`;
}

/**
 * What the app shell hands `MoneyProvider`. The site shows the storefront's
 * currency (`/site-config`), never the wallet's. The ledger currency comes
 * from `/display-rates/current`; before that answers the wallet's, then the
 * site's, stand in — exact for every brand whose two currencies match. A rate
 * is only used for the currency it was quoted for.
 */
export function shellMoney(input: {
  siteCurrency: string | null | undefined;
  displayRate: { displayCurrency: string; ledgerCurrency: string; rate: string | null } | undefined;
  walletCurrency: string | undefined;
  /** site-config's ledger currency, known before the rate endpoint answers and before login. */
  siteLedgerCurrency?: string | null;
}): { currency: string; displayCurrency: string; rate: string | null } {
  const { siteCurrency, displayRate, walletCurrency, siteLedgerCurrency } = input;
  const displayCurrency = siteCurrency || displayRate?.displayCurrency || 'THB';
  const rate =
    displayRate && displayRate.displayCurrency.toUpperCase() === displayCurrency.toUpperCase()
      ? displayRate.rate
      : null;
  return {
    currency: displayRate?.ledgerCurrency ?? (siteLedgerCurrency || undefined) ?? walletCurrency ?? displayCurrency,
    displayCurrency,
    rate,
  };
}
